import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/translationHelpers';
import { useUserGoal } from '../../hooks/useUserGoal';
import { getUserProgress } from '../../services/userProgressService';
import { memoizedRecommendations } from '../../services/memoizedRecommendations';
import { userAuth } from '../../services/userAuth';
import { getUserFirstName, getExamDate } from '../../services/supabaseService';
import { getExamHistory, getExamUnlockState } from '../../services/aiExamUnlockService';
import { testCountTracker } from '../../services/testCountTracker';
import { isUserSubscribed, isAdminOverride, FREE_TOPICS } from '../../services/subscriptionGate';
import { TEST_METADATA } from '../../services/aiCoach';
import type { TestRecommendation } from '../../services/aiCoach';
import { getAllTopicPerformance } from '../../services/topicPerformanceService';
import { buildTopicScoresFromPerformance } from '../AIExam/AIExam.utils';
import { computeReadiness } from '../../services/readinessService';
import { SettingsDropdown } from '../SettingsDropdown';
import { WhatsAppCoachingCTA } from '../WhatsAppCoachingCTA';
import './UserDashboard.css';

interface WeakArea {
  topic: string;
  score: number;
}

interface UserData {
  lastExamScore: number | null;
  weakAreas: WeakArea[];
  completedWeakAreas: string[];
}


type DashboardLang = 'en' | 'nl' | 'ar';

function getDashboardGreeting({
  isSubscribed,
  firstName,
  currentLanguage,
  userGoal,
}: {
  isSubscribed: boolean
  firstName: string | null
  currentLanguage: DashboardLang
  userGoal?: 'first-time' | 'failed' | 'exam-soon' | null
}): {
  line1: string
  line1Name: string | null
  line2: string
} {
  const hour = new Date().getHours()

  const timeGreeting =
    currentLanguage === 'nl'
      ? hour < 12
        ? 'Goedemorgen'
        : hour < 18
          ? 'Goedemiddag'
          : 'Goedenavond'
      : currentLanguage === 'ar'
        ? hour < 12
          ? 'صباح الخير'
          : hour < 18
            ? 'مساء الخير'
            : 'مساء النور'
        : hour < 12
          ? 'Good morning'
          : hour < 18
            ? 'Good afternoon'
            : 'Good evening'

  const safeName =
    isSubscribed && firstName && firstName.trim().length > 0
      ? firstName.trim().split(' ')[0]
      : null

  // Goal-specific greeting line2
  if (userGoal === 'first-time') {
    return {
      line1: timeGreeting,
      line1Name: safeName || '',
      line2:
        currentLanguage === 'nl'
          ? 'Laten we beginnen met de basis.'
          : currentLanguage === 'ar'
            ? 'فلنبدأ بالأساسيات.'
            : "Let's start with the basics.",
    }
  }

  if (userGoal === 'failed') {
    return {
      line1: safeName ? timeGreeting : timeGreeting,
      line1Name: safeName || '',
      line2:
        currentLanguage === 'nl'
          ? 'Laten we zien waar het vorige keer misging.'
          : currentLanguage === 'ar'
            ? 'دعنا نرى أين حدث الخطأ في المرة الأخيرة.'
            : "Let's see where it went wrong last time.",
    }
  }

  // Exam date and countdown live only in the Practice card; keep a neutral subline here.
  const neutralLine2 =
    currentLanguage === 'nl'
      ? 'Ga er volledig voor.'
      : currentLanguage === 'ar'
        ? 'هيا نعطي كل ما لدينا.'
        : "Let's go all in."

  return {
    line1: timeGreeting,
    line1Name: safeName || '',
    line2: neutralLine2,
  }
}

function getPracticeExamContext(
  examDateStr: string | null,
  daysLeft: number | null,
  lang: DashboardLang
): { dateLabel: string; countdown: string; urgent: boolean } | null {
  if (examDateStr == null || daysLeft == null) return null
  const exam = new Date(examDateStr)
  if (Number.isNaN(exam.getTime())) return null

  const formatted =
    lang === 'nl'
      ? exam.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
      : lang === 'ar'
        ? exam.toLocaleDateString('ar', { day: 'numeric', month: 'long', year: 'numeric' })
        : exam.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  const cbrLabel = lang === 'nl' ? 'CBR-examen' : lang === 'ar' ? 'امتحان CBR' : 'CBR exam'
  const dateLabel = `${cbrLabel}: ${formatted}`

  let countdown: string
  if (daysLeft < 0) {
    countdown =
      lang === 'nl'
        ? 'Deze datum is verstreken.'
        : lang === 'ar'
          ? 'هذا التاريخ مضى.'
          : 'This date has passed.'
  } else if (daysLeft === 0) {
    countdown =
      lang === 'nl'
        ? 'Examen vandaag, succes!'
        : lang === 'ar'
          ? 'الامتحان اليوم، حظاً موفقاً!'
          : 'Exam today, good luck!'
  } else if (daysLeft === 1) {
    countdown =
      lang === 'nl'
        ? 'Examen morgen, je bent er klaar voor!'
        : lang === 'ar'
          ? 'الامتحان غداً، أنت مستعد!'
          : 'Exam tomorrow, you are ready!'
  } else if (daysLeft <= 3) {
    countdown =
      lang === 'nl'
        ? `Nog ${daysLeft} dagen, geef alles!`
        : lang === 'ar'
          ? `!${daysLeft} أيام، أعطِ كل ما لديك`
          : `${daysLeft} days left, go all in!`
  } else if (lang === 'nl') {
    countdown = `Nog ${daysLeft} dagen, jij kan het!`
  } else if (lang === 'ar') {
    countdown = `!${daysLeft} يوماً، أنت تستطيع`
  } else {
    countdown = `${daysLeft} days to exam, you can do it!`
  }

  const urgent = daysLeft >= 0 && daysLeft <= 7

  return { dateLabel, countdown, urgent }
}

const DASHBOARD_TEXTS: Record<string, { aiExamReady: string; keepPracticing: string; timeForExam: string; complete3: string; scoreBelow80: string; freeReady: string; moreTopics: string; nextReady: string; keepFindWeak: string }> = {
  en: {
    aiExamReady: "Your personalised exam is ready",
    keepPracticing: "Keep practicing to unlock your personalised exam",
    timeForExam: "Time for your personalised exam",
    complete3: "Complete 3 practice tests to unlock your Personalised Exam",
    scoreBelow80: "Score below 80% on a topic to unlock your personalised exam",
    freeReady: "Your Personalised Exam is ready",
    moreTopics: "[X] more topics to unlock your next exam",
    nextReady: "Your next exam is ready",
    keepFindWeak: "Keep practicing to find your weak areas",
  },
  nl: {
    aiExamReady: "Jouw Persoonlijk Examen staat klaar",
    keepPracticing: "Blijf oefenen om je Persoonlijk Examen te ontgrendelen",
    timeForExam: "Tijd voor jouw Persoonlijk Examen",
    complete3: "Voltooi 3 oefentoetsen om je Persoonlijk Examen te ontgrendelen",
    scoreBelow80: "Scoor onder 80% op een onderwerp om je Persoonlijk Examen te ontgrendelen",
    freeReady: "Jouw Persoonlijk Examen staat klaar",
    moreTopics: "Nog [X] onderwerpen voor je volgende examen",
    nextReady: "Je volgende examen staat klaar",
    keepFindWeak: "Blijf oefenen om je zwakke punten te vinden",
  },
  ar: {
    aiExamReady: "امتحانك جاهز",
    keepPracticing: "واصل التدريب لفتح امتحانك",
    timeForExam: "حان وقت امتحانك",
    complete3: "أكمل 3 اختبارات تدريبية لفتح امتحانك الشخصي",
    scoreBelow80: "احصل على أقل من 80% في موضوع لفتح امتحانك",
    freeReady: "امتحانك الشخصي جاهز",
    moreTopics: "[X] مواضيع أخرى لفتح امتحانك القادم",
    nextReady: "امتحانك القادم جاهز",
    keepFindWeak: "واصل التدريب لإيجاد نقاط ضعفك",
  },
};

export function UserDashboard() {
  const navigate = useNavigate();
  /** AI exam intro (locked or unlocked) — never preserve ?skipIntro from a previous visit */
  const goToAIExamIntro = () => {
    navigate({ pathname: '/ai-exam' });
  };
  const { t_nested, currentLanguage } = useLanguage();
  const { goal: userGoal } = useUserGoal();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiCoachRecommendation, setAiCoachRecommendation] = useState<TestRecommendation | null>(null);
  const [examDate, setExamDate] = useState<string | null>(null);
  const [topicScores, setTopicScores] = useState<ReturnType<typeof buildTopicScoresFromPerformance>>([]);
  const backgroundVideoRef = useRef<HTMLVideoElement | null>(null);

  const [firstName, setFirstName] = useState<string>(() => {
    try {
      const user = userAuth.getCurrentUser();
      return (user?.name || '').trim().split(/\s+/)[0] || '';
    } catch { return ''; }
  });

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as keyof typeof DASHBOARD_TEXTS;
  const dt = DASHBOARD_TEXTS[lang] || DASHBOARD_TEXTS.en;

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setTopicScores(buildTopicScoresFromPerformance([]));
      return;
    }
    getAllTopicPerformance(userId)
      .then((perf) => setTopicScores(buildTopicScoresFromPerformance(perf)))
      .catch(() => setTopicScores(buildTopicScoresFromPerformance([])));
  }, []);

  useEffect(() => {
    const tryPlayVideo = () => {
      const video = backgroundVideoRef.current;
      if (!video) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    tryPlayVideo();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tryPlayVideo();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        tryPlayVideo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);


  useEffect(() => {
    const loadExamDate = async () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const date = await getExamDate(userId);
        setExamDate(date || null);
      } else {
        const pending = localStorage.getItem('EXAM_DATE_PENDING');
        setExamDate(pending || null);
      }
    };
    loadExamDate();
  }, []);

  useEffect(() => {
    try {
      const user = userAuth.getCurrentUser();
      if (user?.id && !user.isGuest) {
        getUserFirstName(user.id).then(name => {
          if (name) setFirstName(name);
        }).catch(() => {});
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const currentUser = userAuth.getCurrentUser();
      const userId = currentUser?.id || 'guest';
      const rec = memoizedRecommendations.getRecommendation(userId, t_nested);
      if (rec && rec.testId) {
        setAiCoachRecommendation(rec);
      } else {
        setAiCoachRecommendation(null);
      }
    } catch {
      setAiCoachRecommendation(null);
    }
  }, [currentLanguage, t_nested]);

  const loadUserData = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setUserData({ lastExamScore: null, weakAreas: [], completedWeakAreas: [] });
        setLoading(false);
        return;
      }
      const progress = await getUserProgress(userId);
      if (progress) {
        setUserData({
          lastExamScore: progress.lastExamScore,
          weakAreas: progress.weakAreas,
          completedWeakAreas: progress.completedWeakAreas
        });
      } else {
        setUserData({ lastExamScore: null, weakAreas: [], completedWeakAreas: [] });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setUserData({ lastExamScore: null, weakAreas: [], completedWeakAreas: [] });
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = isUserSubscribed();
  const examUnlockState = getExamUnlockState();
  const testsCompleted = testCountTracker.getTestCount();
  const freeTierUsed = testsCompleted >= 3;
  const freeAiExamPending = testCountTracker.getFreeAiExamPending();
  const examHistory = getExamHistory();

  // After completing an AI exam, don't immediately re-recommend it (15-min cooldown).
  // The new exam may have been unlocked instantly by maybeUnlockOneExam(), but the
  // user just finished 50 questions and should be directed to practice tests first.
  const justCompletedExam = (() => {
    if (examHistory.length === 0) return false;
    const lastDate = examHistory[0]?.date;
    if (!lastDate) return false;
    const FIFTEEN_MINUTES = 15 * 60 * 1000;
    return Date.now() - new Date(lastDate).getTime() < FIFTEEN_MINUTES;
  })();

  const safeWeakAreas = Array.isArray(userData?.weakAreas) ? userData.weakAreas : [];
  const safeCompletedWeakAreas = Array.isArray(userData?.completedWeakAreas) ? userData.completedWeakAreas : [];
  const remainingWeakAreas = safeWeakAreas.filter(
    area => !safeCompletedWeakAreas.includes(area.topic)
  );
  const weakestArea = remainingWeakAreas.sort((a, b) => a.score - b.score)[0];

  const formatTestName = (name: string) => name.replace(/-/g, ' ');
  const TEST_ID_TO_I18N: Record<string, string> = {
    'traffic-lights-signals': 'testNames.trafficLightsSignals',
    'road-signs': 'testNames.roadSigns',
    'priority-rules': 'testNames.priorityRules',
    'hazard-perception': 'testNames.hazardPerception',
    'speed-safety': 'testNames.speedSafety',
    'bicycle-interactions': 'testNames.bicycleInteractions',
    'roundabout-rules': 'testNames.roundaboutRules',
    'tram-interactions': 'testNames.tramInteractions',
    'pedestrian-crossings': 'testNames.pedestrianCrossings',
    'construction-zones': 'testNames.constructionZones',
    'weather-conditions': 'testNames.weatherConditions',
    'motorway-rules': 'testNames.motorwayRules',
    'vehicle-knowledge': 'testNames.vehicleKnowledge',
    'parking-rules': 'testNames.parkingRules',
    'environmental': 'testNames.environmental',
    'technology-safety': 'testNames.technologySafety',
    'alcohol-drugs': 'testNames.alcoholDrugs',
    'fatigue-rest': 'testNames.fatigueRest',
    'emergency-procedures': 'testNames.emergencyProcedures',
  };
  const getTranslatedTopicName = (testId: string) =>
    getTranslation(t_nested, TEST_ID_TO_I18N[testId] || 'testNames.trafficLightsSignals', TEST_METADATA[testId]?.name || testId.replace(/-/g, ' '));
  const firstFreeTopic = FREE_TOPICS[0]
    ? { id: FREE_TOPICS[0], name: getTranslatedTopicName(FREE_TOPICS[0]) }
    : null;

  let displayRecommendation: { name: string; id: string; score?: number } | null = null;
  let recSubtitle = '';
  const genericSubtitle = getTranslation(t_nested, 'practice.recommendedForYou', 'Recommended based on your progress');

  const hasSituationAnswered = () => {
    const situationAtTest8 = localStorage.getItem('situationAtTest8');
    const situationTest8Skipped = localStorage.getItem('situationTest8Skipped') === 'true';
    return !!situationAtTest8 || situationTest8Skipped;
  };
  const userHasAnsweredSituation = hasSituationAnswered();

  if (isSubscribed && !isAdminOverride()) {
    if (examUnlockState.activeExamInstanceId && !justCompletedExam && userHasAnsweredSituation) {
      displayRecommendation = { name: t_nested('navigation.aiExam') || 'AI Exam', id: 'ai-exam' };
      recSubtitle = dt.timeForExam;
    } else if (aiCoachRecommendation?.testId && aiCoachRecommendation.testId !== 'ai-exam' && userHasAnsweredSituation) {
      // Never show 'ai-exam' from aiCoach cache when no active exam instance exists —
      // the cache may be stale right after the user just completed their AI exam.
      displayRecommendation = {
        name: getTranslatedTopicName(aiCoachRecommendation.testId),
        id: aiCoachRecommendation.testId,
        score: aiCoachRecommendation.score,
      };
      recSubtitle = genericSubtitle;
    } else if (weakestArea && userHasAnsweredSituation) {
      displayRecommendation = {
        name: formatTestName(weakestArea.topic),
        id: weakestArea.topic.toLowerCase().replace(/\s+/g, '-'),
        score: weakestArea.score,
      };
      recSubtitle = genericSubtitle;
    } else {
      displayRecommendation = firstFreeTopic ? { name: firstFreeTopic.name, id: firstFreeTopic.id } : null;
      recSubtitle = genericSubtitle;
    }
  } else {
    if (!isAdminOverride()) {
      const completed = testCountTracker.getTestCount();
      if (completed < 3) {
        const nextId = FREE_TOPICS[completed];
        displayRecommendation = nextId
          ? { name: getTranslatedTopicName(nextId), id: nextId }
          : null;
        recSubtitle = genericSubtitle;
      } else if (completed >= 3 && freeAiExamPending) {
        displayRecommendation = { name: t_nested('navigation.aiExam') || 'AI Exam', id: 'ai-exam' };
        recSubtitle = userHasAnsweredSituation ? dt.aiExamReady : genericSubtitle;
      } else if (completed >= 3 && !freeAiExamPending) {
        displayRecommendation = firstFreeTopic ? { name: firstFreeTopic.name, id: firstFreeTopic.id } : null;
        recSubtitle = userHasAnsweredSituation ? dt.keepPracticing : genericSubtitle;
      } else {
        displayRecommendation = firstFreeTopic ? { name: firstFreeTopic.name, id: firstFreeTopic.id } : null;
        recSubtitle = genericSubtitle;
      }
    } else {
      if (aiCoachRecommendation?.testId && aiCoachRecommendation.testId !== 'ai-exam' && userHasAnsweredSituation) {
        displayRecommendation = {
          name: getTranslatedTopicName(aiCoachRecommendation.testId),
          id: aiCoachRecommendation.testId,
          score: aiCoachRecommendation.score,
        };
      } else if (weakestArea && userHasAnsweredSituation) {
        displayRecommendation = {
          name: formatTestName(weakestArea.topic),
          id: weakestArea.topic.toLowerCase().replace(/\s+/g, '-'),
          score: weakestArea.score,
        };
      } else {
        displayRecommendation = firstFreeTopic ? { name: firstFreeTopic.name, id: firstFreeTopic.id } : null;
      }
      recSubtitle = genericSubtitle;
    }
  }

  const recommendationIsAiExam = displayRecommendation?.id === 'ai-exam';

  // Weak topics (pattern-based) from Supabase topic performance, excluding mastered topics (device-level)
  const weakestTopicFromPerformance = (() => {
    const weakest = [...topicScores]
      .filter((t) => t.hasRealData)
      .filter((t) => {
        try {
          return localStorage.getItem(`weakTopicMastered:${t.topicId}`) !== 'true';
        } catch {
          return true;
        }
      })
      .sort((a, b) => a.accuracy - b.accuracy)[0];
    return weakest && weakest.accuracy < 70 ? weakest : null;
  })();

  // If we have a true weak topic, prefer it in the recommendation box (unless AI exam is ready)
  if (
    userHasAnsweredSituation &&
    !recommendationIsAiExam &&
    weakestTopicFromPerformance &&
    displayRecommendation?.id !== weakestTopicFromPerformance.topicId
  ) {
    displayRecommendation = {
      id: weakestTopicFromPerformance.topicId,
      name: getTranslatedTopicName(weakestTopicFromPerformance.topicId),
      score: weakestTopicFromPerformance.accuracy,
    };
    recSubtitle = getTranslation(t_nested, 'dashboard.weakTopicsRecommended', 'Recommended from your weak topics');
  }

  const _handleStartRecommendation = () => {
    if (!isSubscribed && freeTierUsed) {
      navigate('/subscription');
      return;
    }
    const target = displayRecommendation || (firstFreeTopic ? { name: firstFreeTopic.name, id: firstFreeTopic.id } : null);
    if (!target) {
      navigate('/tests');
      return;
    }
    if (target.id === 'ai-exam') {
      goToAIExamIntro();
    } else if (target.id === 'tests') {
      navigate('/tests');
    } else {
      const testId = target.id;
      if (localStorage.getItem('practiceIntroSeen') === 'true') {
        navigate(`/practice/${testId}`, { replace: true });
      } else {
        navigate('/practice-bridge', { state: { source: 'recommendation', testId } });
      }
    }
  };

  const daysLeft = examDate && userHasAnsweredSituation
    ? Math.ceil(
        (new Date(examDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      )
    : null;

  const greeting = getDashboardGreeting({
    isSubscribed,
    firstName: firstName || null,
    currentLanguage: lang as DashboardLang,
    userGoal: userHasAnsweredSituation ? userGoal : null,
  })
  const greetingLine1 = greeting.line1
  const greetingLine1Name = greeting.line1Name
  const greetingLine2 = greeting.line2

  const practiceExamContext = userHasAnsweredSituation
    ? getPracticeExamContext(
        examDate,
        daysLeft,
        lang as DashboardLang
      )
    : null

  const aiExamLabel = getTranslation(t_nested, 'navigation.aiExam', 'Personalised Exam');
  const recommendedLabel = getTranslation(t_nested, 'practice.recommendedForYou', 'Recommended for You');
  const _startLabel = getTranslation(t_nested, 'quiz.start', 'Start');

  const readiness = computeReadiness(topicScores);
  const readinessLabel = getTranslation(t_nested, 'dashboard.readinessLabel', 'Readiness');
  const studyTonightLabel = getTranslation(t_nested, 'dashboard.studyTonight', 'Study tonight');
  const readinessNoData = getTranslation(t_nested, 'dashboard.readinessNoData', 'Complete a few practice tests to get a readiness score.');
  const readinessTonightEmpty = getTranslation(t_nested, 'dashboard.studyTonightEmpty', 'Do 1 mixed practice test.');
  const tonightTopics = readiness.studyTonightTopicIds
    .slice(0, 2)
    .map((id) => getTranslatedTopicName(id))
    .filter(Boolean);

  const dashboardLoadingAriaLabel =
    lang === 'nl'
      ? 'Dashboard laden'
      : lang === 'ar'
        ? 'جاري تحميل لوحة التحكم'
        : 'Loading dashboard';

  if (loading || !userData) {
    return (
      <div
        className="user-dashboard safe-top"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={dashboardLoadingAriaLabel}
      >
        <div className="dashboard-inner dashboard-skeleton" data-card-count="3">
          <div className="dashboard-card recommended-card skeleton-card">
            <div className="skeleton-line skeleton-line--title" />
            <div className="skeleton-line skeleton-line--subtitle" />
          </div>
          <div className="dashboard-card skeleton-card">
            <div className="skeleton-line skeleton-line--label" />
            <div className="skeleton-line skeleton-line--title" />
            <div className="skeleton-line skeleton-line--body" />
            <div className="skeleton-btn" />
          </div>
          <div className="dashboard-card skeleton-card">
            <div className="skeleton-line skeleton-line--title" />
            <div className="skeleton-line skeleton-line--body" />
            <div className="skeleton-progress" />
            <div className="skeleton-line skeleton-line--small" />
          </div>
        </div>
      </div>
    );
  }

  const showSubscribedAiExamProgressBar =
    isSubscribed && !isAdminOverride() && examUnlockState.activeExamInstanceId === null;
  const totalTestsForAiProgress = testCountTracker.getTestCount();
  const nextThresholdAiProgress = Math.ceil(Math.max(totalTestsForAiProgress, 1) / 10) * 10;
  const progressWithinTen =
    totalTestsForAiProgress % 10 === 0 ? 10 : totalTestsForAiProgress % 10;
  const remainingUntilAiThreshold = nextThresholdAiProgress - totalTestsForAiProgress;
  const subscribedAiProgressLabel =
    lang === 'nl'
      ? `${progressWithinTen} / 10 toetsen tot je AI-examen`
      : lang === 'ar'
        ? `${progressWithinTen} / 10 اختبارات حتى امتحانك الذكي`
        : `${progressWithinTen} / 10 tests until your AI Exam`;

  const visibleCardCount = 3;

  // State-specific content for recommended card
  // Only show goal-specific messaging if user has answered situation modal
  const getPillText = () => {
    if (!userHasAnsweredSituation) {
      if (lang === 'nl') return 'AANBEVOLEN';
      if (lang === 'ar') return 'موصى به';
      return 'RECOMMENDED';
    }

    if (lang === 'nl') {
      if (userGoal === 'first-time') return 'BEGIN HIER';
      if (userGoal === 'failed') return 'MEEST KRITIEK VOOR JOU';
      if (userGoal === 'exam-soon') return 'HOOGSTE PRIORITEIT';
      return 'AANBEVOLEN';
    }
    if (lang === 'ar') {
      if (userGoal === 'first-time') return 'ابدأ هنا';
      if (userGoal === 'failed') return 'الأكثر حيوية بالنسبة لك';
      if (userGoal === 'exam-soon') return 'أعلى أولوية';
      return 'موصى به';
    }
    // English
    if (userGoal === 'first-time') return 'BEGIN HERE';
    if (userGoal === 'failed') return 'MOST CRITICAL FOR YOU';
    if (userGoal === 'exam-soon') return 'HIGHEST PRIORITY';
    return 'RECOMMENDED';
  };

  const getPillDotColor = () => {
    if (!userHasAnsweredSituation) return '#0F172A';
    if (userGoal === 'first-time') return '#0F172A';
    if (userGoal === 'failed') return '#DC2626';
    if (userGoal === 'exam-soon') return '#C47F0A';
    return '#0F172A';
  };

  const getMetaText = () => {
    if (lang === 'nl') {
      return '10 vragen · ~5 minuten';
    }
    if (lang === 'ar') {
      return '10 أسئلة · ~5 دقائق';
    }
    return '10 questions · ~5 minutes';
  };

  const getButtonText = () => {
    if (lang === 'nl') {
      if (userGoal === 'first-time') return 'Start eerste toets';
      if (userGoal === 'failed') return 'Start diagnosetoets';
      if (userGoal === 'exam-soon') return 'Start nu';
    }
    if (lang === 'ar') {
      if (userGoal === 'first-time') return 'ابدأ الاختبار الأول';
      if (userGoal === 'failed') return 'ابدأ اختبار التشخيص';
      if (userGoal === 'exam-soon') return 'ابدأ الآن';
    }
    return getTranslation(t_nested, 'quiz.start', 'Start');
  };

  const getButtonIcon = () => {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5 3 19 12 5 21 5 3"></polygon>
      </svg>
    );
  };

  // AI Exam card state-specific content
  const getAiExamSubline = () => {
    if (lang === 'nl') {
      if (userGoal === 'first-time') return 'Nog 3 toetsen om te ontgrendelen';
      if (userGoal === 'failed') return 'Vindt precies waar het vorige keer misging';
      if (userGoal === 'exam-soon') return 'Klaar na 3 korte toetsen · ~15 min';
      return '';
    }
    if (lang === 'ar') {
      if (userGoal === 'first-time') return '3 اختبارات أخرى للتفتيح';
      if (userGoal === 'failed') return 'ابحث بالضبط عن مكان الخطأ';
      if (userGoal === 'exam-soon') return 'جاهز بعد 3 اختبارات قصيرة · ~15 دقيقة';
      return '';
    }
    // English
    if (userGoal === 'first-time') return '3 more tests to unlock';
    if (userGoal === 'failed') return 'Find exactly where you went wrong';
    if (userGoal === 'exam-soon') return 'Ready after 3 quick tests · ~15 min';
    return '';
  };

  const getAiExamFeatures = () => {
    if (lang === 'nl') {
      return [
        'AI identificeert jouw zwakke punten',
        'Examen gebouwd op jouw zwakke gebieden',
        'CBR-formaat',
      ];
    }
    if (lang === 'ar') {
      return [
        'يحدد الذكاء الاصطناعي نقاط ضعفك',
        'امتحان مبني على مناطق ضعفك',
        'صيغة CBR',
      ];
    }
    return [
      'AI identifies your weaker areas',
      'Exam built on your weaker areas',
      'CBR format',
    ];
  };

  return (
    <div className="user-dashboard safe-top">
      {/* Video Background */}
      <video
        ref={backgroundVideoRef}
        className="dashboard-bg-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      >
        <source src="/videos/signin-intro.mp4" type="video/mp4" />
      </video>
      <div className="dashboard-inner" data-card-count={visibleCardCount}>
        <div className="dashboard-greeting">
          <div className="dashboard-greeting-top">
            <div className="dashboard-greeting-text">
              <span className="dashboard-greeting-prefix">{greetingLine1}</span>
              {greetingLine1Name && (
                <>
                  <span>, </span>
                  <span className="dashboard-greeting-name">{greetingLine1Name}</span>
                </>
              )}
              <span> 👋</span>
            </div>
            <div className="dashboard-greeting-right">
              {!userAuth.isLoggedIn() && (
                <div className="dashboard-auth-pills">
                  <button
                    onClick={() => navigate('/login')}
                    className="auth-pill auth-pill-signin"
                    type="button"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className="auth-pill auth-pill-signup"
                    type="button"
                  >
                    Sign Up
                  </button>
                </div>
              )}
              {userAuth.isLoggedIn() && (
                <button
                  onClick={() => navigate('/subscription')}
                  className="auth-pill auth-pill-upgrade"
                  type="button"
                >
                  Upgrade
                </button>
              )}
              <div className="dashboard-greeting-settings">
                <SettingsDropdown />
              </div>
            </div>
          </div>
          {!userGoal && (
            <p
              className="dashboard-greeting-subline-pill"
              role="status"
            >
              {greetingLine2}
            </p>
          )}
        </div>

        {displayRecommendation?.id !== 'ai-exam' ? (
          <div className="dashboard-card recommended-card">
            {/* Branding Section */}
            <div className="card-branding-simple">
              <h1 className="branding-title">
                <span className="branding-primary">THEORY</span><span className="branding-secondary">COACH</span>
              </h1>
              <p className="branding-tagline">
                {getTranslation(
                  t_nested,
                  'dashboard.brandingTagline',
                  'AI Dutch driving theory coach'
                )}
              </p>
            </div>

            {/* Pill label */}
            <div className="dashboard-pill pill-recommendation">
              <div className="dashboard-pill-dot" style={{ backgroundColor: getPillDotColor() }}></div>
              <span>{getPillText()}</span>
            </div>

            {/* Topic name */}
            <h2 className="dashboard-topic">
              {displayRecommendation ? displayRecommendation.name.toUpperCase() : (firstFreeTopic?.name ?? 'Practice').toUpperCase()}
            </h2>

            {/* Meta line */}
            <p className="dashboard-meta">{getMetaText()}</p>

            {/* Start button */}
            <button
              type="button"
              className="dashboard-start-btn"
              onClick={_handleStartRecommendation}
            >
              {getButtonIcon()}
              {getButtonText()}
            </button>
          </div>
        ) : (
          <div className="dashboard-card recommended-card">
            <div className="dashboard-pill pill-ai-exam">
              <div className="dashboard-pill-dot" style={{ backgroundColor: '#0F172A' }}></div>
              <span>{recommendedLabel}</span>
            </div>
            <h2 className="dashboard-topic">
              {aiExamLabel}
            </h2>
            <div className="dashboard-reason-tag">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
              <span>{recSubtitle}</span>
            </div>
            <button
              type="button"
              className="dashboard-start-btn"
              onClick={_handleStartRecommendation}
            >
              {getButtonIcon()}
              {'Start'}
            </button>
          </div>
        )}

        {/* Practice Card */}
        <div className="dashboard-card practice-card">
          <div className="dashboard-pill pill-practice">
            <div className="dashboard-pill-dot"></div>
            <span>{lang === 'nl' ? 'OEFENEN' : lang === 'ar' ? 'تمرين' : 'PRACTICE'}</span>
          </div>
          <h2 className="dashboard-topic">
            {lang === 'nl' ? 'Oefenen' : lang === 'ar' ? 'تمرين' : 'Practice'}
          </h2>
          <p className="dashboard-meta">
            {lang === 'nl' ? 'Beoefening toetsen · Alle onderwerpen' : lang === 'ar' ? 'اختبارات التدريب · جميع المواضيع' : 'Practice tests · All topics'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {readiness.hasRealData ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                  <span>{readinessLabel}</span>
                  <span style={{ fontWeight: 700 }}>{readiness.readinessPercent}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
                  <span>{studyTonightLabel}</span>
                  <span style={{ fontWeight: 600, textAlign: 'end' }}>
                    {tonightTopics.length > 0 ? tonightTopics.join(' · ') : readinessTonightEmpty}
                  </span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>{readinessNoData}</div>
            )}
          </div>
          <button
            type="button"
            className="dashboard-start-btn"
            onClick={() => navigate('/practice')}
          >
            {lang === 'nl' ? 'Oefenen' : lang === 'ar' ? 'تمرين' : 'Practice'}
          </button>
          {isSubscribed && (
            <WhatsAppCoachingCTA />
          )}
        </div>

        {/* AI Exam Card */}
        <div className={`dashboard-card ai-exam-card${recommendationIsAiExam ? ' ai-exam-card--secondary' : ''}`}>
          {/* AI Exam Header */}
          <div className="dashboard-ai-exam-header">
            <div className="dashboard-ai-exam-icon-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4M12 8h.01"></path>
              </svg>
            </div>
            <div className="dashboard-ai-exam-header-text">
              <h3 className="dashboard-ai-exam-title">
                {getTranslation(t_nested, 'navigation.aiExam', 'AI Exam')}
              </h3>
              {getAiExamSubline() && (
                <p className="dashboard-ai-exam-subline">{getAiExamSubline()}</p>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="dashboard-ai-exam-progress">
            <div className="dashboard-ai-exam-progress-track">
              <div className="dashboard-ai-exam-progress-fill" style={{ width: '0%' }}></div>
            </div>
            <span className="dashboard-ai-exam-progress-counter">0 / 3</span>
          </div>

          {/* Exam date and situation */}
          {practiceExamContext && (
            <div
              className={`practice-exam-context${
                practiceExamContext.urgent ? ' practice-exam-context--urgent' : ''
              }`}
            >
              <p className="practice-exam-context-date">{practiceExamContext.dateLabel}</p>
              <p className="practice-exam-context-countdown">{practiceExamContext.countdown}</p>
            </div>
          )}

          {/* Features Teaser */}
          <div className="dashboard-ai-exam-teaser">
            <h4 className="dashboard-ai-exam-teaser-title">{lang === 'nl' ? 'Na ontgrendeling krijg je:' : lang === 'ar' ? 'بعد الإفتتاح:' : 'After unlocking:'}</h4>
            {getAiExamFeatures().map((feature, idx) => (
              <div key={idx} className="dashboard-ai-exam-feature">
                <span className="dashboard-ai-exam-feature-arrow">→</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* AI Exam CTA Button - Locked/Unlocked State */}
          <div className="dashboard-ai-exam-cta-section">
            {examUnlockState.activeExamInstanceId ? (
              <button
                type="button"
                className="dashboard-ai-exam-cta-btn dashboard-ai-exam-cta-btn--unlocked"
                onClick={goToAIExamIntro}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                {lang === 'nl' ? 'Start' : lang === 'ar' ? 'ابدأ' : 'Start'}
              </button>
            ) : (
              <button
                type="button"
                className="dashboard-ai-exam-cta-btn dashboard-ai-exam-cta-btn--locked"
                disabled
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                {lang === 'nl' ? 'Examen Vergrendeld' : lang === 'ar' ? 'الامتحان مقفول' : 'Exam Locked'}
              </button>
            )}
          </div>
        </div>

        {showSubscribedAiExamProgressBar && (
          <div
            className="ai-exam-progress"
            aria-label={`${remainingUntilAiThreshold} until next AI exam threshold`}
          >
            <div className="ai-exam-progress-text">{subscribedAiProgressLabel}</div>
            <div className="ai-exam-progress-bar">
              <div
                className="ai-exam-progress-fill"
                style={{ width: `${(progressWithinTen / 10) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
